/*
 * PersistentSolRadio — the single, application-lifetime SolRadio player.
 *
 * WHY THIS EXISTS
 * ───────────────
 * Each Hub page (`/hub`, `/hub/marketplace`, `/hub/games`, …) is a
 * separate top-level route in `App.js`. Prior to this refactor, the
 * SolRadio player lived inside `HubSidebar`, which is rendered inside
 * every Hub page's `HubHeader`. Every route change therefore unmounted
 * the entire page tree, destroyed the player, and killed the media
 * session — the user had to press Play again on every route change.
 *
 * ARCHITECTURAL FIX
 * ─────────────────
 * This component is rendered **once**, in `App.js`, as a sibling of
 * `<Routes>`. React never unmounts it during navigation. The audio
 * element it holds is the ONLY audio source in the app; its media
 * session survives every route change and every sidebar toggle.
 *
 * WHY A NATIVE PLAYER, NOT AN IFRAME
 * ──────────────────────────────────
 * The collapsed-sidebar button must control playback directly and
 * reflect the current Play/Pause state. That is impossible with a
 * cross-origin iframe (SolRadio's `/embed` page does not expose a
 * postMessage command channel), so we play the SolRadio stream
 * directly via an HTML5 `<audio>` element. The stream, cover art,
 * and Now-Playing metadata all come from SolRadio's public Azuracast
 * station endpoints (CORS-enabled: `access-control-allow-origin: *`).
 * The visual matches the official SolRadio embed 1:1.
 *
 * COORDINATION WITH HubSidebar
 * ────────────────────────────
 * Sidebar collapse state lives in localStorage under
 * `hub.sidebar.collapsed` and is broadcast via a
 * `hub-sidebar:toggle` window event. This component subscribes so
 * the visual width tracks the sidebar exactly. On non-Hub routes and
 * mobile the player is visually hidden (opacity 0, pointer-events
 * none, translateX(-100%)) but the `<audio>` element is NEVER
 * unmounted — playback survives leaving and re-entering the Hub.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import "./PersistentSolRadio.css";

const LS_COLLAPSED = "hub.sidebar.collapsed";
const LS_VOLUME    = "hub.solradio.volume";
const TOGGLE_EVT   = "hub-sidebar:toggle";

// Public SolRadio Azuracast endpoints. These are the exact resources
// the official /embed page consumes, and they carry the CORS header
// `access-control-allow-origin: *` so we can use them from any
// origin without a proxy.
const STREAM_MP3 =
  "https://azuracast.solradio.fun/listen/solradio_live/radio.mp3";
const NOWPLAYING_URL =
  "https://azuracast.solradio.fun/api/nowplaying_static/solradio_live.json";
const NOWPLAYING_POLL_MS = 5000;

const DEFAULT_VOLUME = 0.7;

function readCollapsed() {
  try { return window.localStorage.getItem(LS_COLLAPSED) === "1"; }
  catch { return false; }
}
function writeCollapsedAndBroadcast(v) {
  try { window.localStorage.setItem(LS_COLLAPSED, v ? "1" : "0"); } catch {
    /* localStorage unavailable — ephemeral is fine */
  }
  try { window.dispatchEvent(new Event(TOGGLE_EVT)); } catch {
    /* SSR / non-DOM — noop */
  }
}
function readVolume() {
  try {
    const raw = window.localStorage.getItem(LS_VOLUME);
    if (raw === null) return DEFAULT_VOLUME;
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT_VOLUME;
    return Math.max(0, Math.min(1, n));
  } catch { return DEFAULT_VOLUME; }
}
function writeVolume(v) {
  try { window.localStorage.setItem(LS_VOLUME, String(v)); } catch {
    /* localStorage unavailable — ephemeral is fine */
  }
}


// ── Icons (inline SVG so the button matches SolRadio's embed 1:1) ──
function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
         aria-hidden="true">
      <path d="M3 1.5l9 5.5-9 5.5V1.5z" fill="#000" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
         aria-hidden="true">
      <rect x="2" y="1.5" width="3.5" height="11" rx="0.6" fill="#000" />
      <rect x="8.5" y="1.5" width="3.5" height="11" rx="0.6" fill="#000" />
    </svg>
  );
}
function BarsIdle() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none"
         aria-hidden="true">
      <rect x="4"  y="14" width="4" height="8"  rx="2" fill="rgba(255,255,255,0.18)" />
      <rect x="10" y="10" width="4" height="16" rx="2" fill="rgba(255,255,255,0.25)" />
      <rect x="16" y="6"  width="4" height="24" rx="2" fill="rgba(255,255,255,0.35)" />
      <rect x="22" y="10" width="4" height="16" rx="2" fill="rgba(255,255,255,0.25)" />
      <rect x="28" y="14" width="4" height="8"  rx="2" fill="rgba(255,255,255,0.18)" />
    </svg>
  );
}
function VolumeIcon({ level }) {
  // Speaker glyph with 0/1/2 wave arcs based on volume level.
  // level 0 = muted (X mark), 1 = low, 2 = medium+, 3 = high.
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
         aria-hidden="true">
      <path d="M2 6h2.5L8 3v10L4.5 10H2V6z"
            fill="rgba(255,255,255,0.55)" />
      {level === 0 && (
        <path d="M11 6l4 4M15 6l-4 4"
              stroke="rgba(255,255,255,0.55)"
              strokeWidth="1.3" strokeLinecap="round" />
      )}
      {level >= 1 && (
        <path d="M10.5 6.4c.7.4 1.1 1.1 1.1 1.9s-.4 1.5-1.1 1.9"
              stroke="rgba(20,241,149,0.85)"
              strokeWidth="1.3" strokeLinecap="round" fill="none" />
      )}
      {level >= 2 && (
        <path d="M12.4 4.8c1.4.7 2.3 2.1 2.3 3.5s-.9 2.8-2.3 3.5"
              stroke="rgba(20,241,149,0.70)"
              strokeWidth="1.3" strokeLinecap="round" fill="none" />
      )}
    </svg>
  );
}
function volumeLevel(v) {
  if (v <= 0.001) return 0;
  if (v < 0.34)   return 1;
  if (v < 0.67)   return 2;
  return 3;
}


export default function PersistentSolRadio() {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed]   = useState(readCollapsed);
  const [playing, setPlaying]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [meta, setMeta]             = useState({ title: null, artist: null, art: null });
  const [artFail, setArtFail]       = useState(false);
  const [volume, setVolume]         = useState(readVolume);
  const [preMuteVolume, setPreMuteVolume] = useState(() => {
    const v = readVolume();
    return v > 0 ? v : DEFAULT_VOLUME;
  });
  const audioRef                    = useRef(null);
  const inHub = pathname === "/hub" || pathname.startsWith("/hub/");
  // Reference to the mobile mini-player slot rendered by
  // `HubHeader` (see `#hub-mobile-radio-slot`). We portal the mini
  // card into this element so the SAME audio + play state powers it.
  // The slot only appears on authenticated mobile views, so this
  // state can be null on desktop or logged-out sessions.
  const [mobileSlot, setMobileSlot] = useState(null);

  // ── Sidebar collapse state sync ─────────────────────────────────
  useEffect(() => {
    const h = () => setCollapsed(readCollapsed());
    window.addEventListener(TOGGLE_EVT, h);
    const s = (e) => { if (e.key === LS_COLLAPSED) setCollapsed(readCollapsed()); };
    window.addEventListener("storage", s);
    return () => {
      window.removeEventListener(TOGGLE_EVT, h);
      window.removeEventListener("storage", s);
    };
  }, []);

  // ── Mobile mini-player slot tracker ─────────────────────────────
  // The mobile header renders `<div id="hub-mobile-radio-slot" />`
  // only when the viewer is authenticated. We attach a portal to it
  // when it appears and clear the portal target when it goes away
  // (e.g. logout, or navigating away from a Hub route). MutationObserver
  // catches both the initial mount and any React reconciliation
  // between authenticated / unauthenticated headers.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const find = () => document.getElementById("hub-mobile-radio-slot");
    setMobileSlot(find());
    const mo = new MutationObserver(() => {
      const el = find();
      setMobileSlot((cur) => (cur === el ? cur : el));
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  // ── Now-playing metadata poller ─────────────────────────────────
  // Only polls while the audio is actually playing to avoid needless
  // traffic; snapshots once at mount so the expanded UI can show the
  // current track even before the user presses Play.
  useEffect(() => {
    let cancelled = false;
    const fetchNow = async () => {
      try {
        const r = await fetch(`${NOWPLAYING_URL}?_t=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        const song = (j.now_playing && j.now_playing.song) || {};
        const nextArt = song.art || null;
        setMeta(cur => {
          const same = cur.title === (song.title || null)
                    && cur.artist === (song.artist || null)
                    && cur.art === nextArt;
          if (same) return cur;
          return { title: song.title || null, artist: song.artist || null, art: nextArt };
        });
        setArtFail(false);
      } catch { /* transient network — retry next tick */ }
    };
    fetchNow();
    if (!playing) return () => { cancelled = true; };
    const iv = setInterval(fetchNow, NOWPLAYING_POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [playing]);

  // ── Audio-element event wiring ──────────────────────────────────
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const onPlay    = () => { setPlaying(true);  setLoading(false); };
    const onPause   = () => { setPlaying(false); setLoading(false); };
    const onWaiting = () => setLoading(true);
    const onCanPlay = () => setLoading(false);
    const onError   = () => { setPlaying(false); setLoading(false); };
    a.addEventListener("playing", onPlay);
    a.addEventListener("pause",   onPause);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("canplay", onCanPlay);
    a.addEventListener("error",   onError);
    return () => {
      a.removeEventListener("playing", onPlay);
      a.removeEventListener("pause",   onPause);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("canplay", onCanPlay);
      a.removeEventListener("error",   onError);
    };
  }, []);

  // ── Apply volume to the audio element + persist to localStorage ─
  useEffect(() => {
    const a = audioRef.current;
    if (a) a.volume = volume;
    writeVolume(volume);
  }, [volume]);

  // ── Play / pause toggle ─────────────────────────────────────────
  // Always resets the src on Play so we resume a fresh live stream
  // rather than a stale buffered fragment.
  const togglePlay = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing || loading) {
      a.pause();
      // Free the buffer so the next Play starts at the live edge.
      try { a.removeAttribute("src"); a.load(); } catch { /* noop */ }
      setPlaying(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      a.src = `${STREAM_MP3}?_t=${Date.now()}`;
      await a.play();
    } catch {
      setPlaying(false);
      setLoading(false);
    }
  }, [playing, loading]);

  // ── Compact icon click (collapsed state) ────────────────────────
  // Per spec: this button controls Play/Pause and MUST NOT re-open
  // the sidebar. It is exactly the same green circular button used
  // in the expanded state, just standalone.
  const onCollapsedButtonClick = useCallback((e) => {
    e.stopPropagation();
    togglePlay();
  }, [togglePlay]);

  // ── Volume controls ─────────────────────────────────────────────
  const onVolumeInput = useCallback((e) => {
    const v = Number(e.target.value) / 100;
    setVolume(v);
    if (v > 0) setPreMuteVolume(v);
  }, []);
  const toggleMute = useCallback(() => {
    setVolume(cur => {
      if (cur > 0) {
        setPreMuteVolume(cur);
        return 0;
      }
      // Un-mute back to whatever the user's last non-zero level was.
      return preMuteVolume > 0 ? preMuteVolume : DEFAULT_VOLUME;
    });
  }, [preMuteVolume]);

  const rootCls =
    "psr" +
    (collapsed ? " psr--collapsed" : " psr--expanded") +
    (inHub ? " psr--visible" : " psr--hidden");

  const title  = meta.title  || "Loading…";
  const artist = meta.artist || "";
  const showArt = !!meta.art && !artFail;

  return (
    <div className={rootCls}
         data-testid="persistent-solradio"
         data-in-hub={inHub ? "1" : "0"}
         data-collapsed={collapsed ? "1" : "0"}
         data-playing={playing ? "1" : "0"}
         aria-hidden={inHub ? "false" : "true"}>

      {/* Native audio element — the single audio source for the app.
          Never conditionally rendered; never gets `display: none` on
          any ancestor while playing. Muted flag off, controls off. */}
      <audio ref={audioRef}
             preload="none"
             crossOrigin="anonymous"
             data-testid="hub-sidebar-radio-audio" />

      {/* ─── Collapsed: only the green circular play/pause button ─── */}
      {collapsed && (
        <button type="button"
                className="psr__play-btn psr__play-btn--collapsed"
                onClick={onCollapsedButtonClick}
                title={playing ? "Pause" : "Play"}
                aria-label={playing ? "Pause SolRadio" : "Play SolRadio"}
                data-testid="hub-sidebar-radio-icon">
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>
      )}

      {/* ─── Expanded: full SolRadio-style card ─── */}
      <div className="psr__frame"
           data-testid="hub-sidebar-radio-frame"
           aria-hidden={collapsed ? "true" : "false"}>
        <div className="psr__card">
          <div className="psr__art">
            {showArt
              ? <img src={meta.art} alt=""
                     onError={() => setArtFail(true)} />
              : <div className="psr__art-fallback"><BarsIdle /></div>}
          </div>
          <div className="psr__meta">
            <div className="psr__meta-top">
              <span className="psr__brand">SOLRADIO</span>
            </div>
            <p className="psr__track"
               title={artist ? `${title} — ${artist}` : title}>
              {title}
            </p>
            {/* Tiny SolRadio-style volume slider — sits under the
                track title so it never squeezes the meta column.
                Persists to localStorage; muting remembers the last
                non-zero level so un-mute restores it. */}
            <div className="psr__vol" data-testid="hub-sidebar-radio-volume">
              <button type="button"
                      className="psr__vol-icon-btn"
                      onClick={toggleMute}
                      title={volume === 0 ? "Un-mute" : "Mute"}
                      aria-label={volume === 0 ? "Un-mute SolRadio" : "Mute SolRadio"}
                      data-testid="hub-sidebar-radio-mute">
                <VolumeIcon level={volumeLevel(volume)} />
              </button>
              <input type="range"
                     className="psr__vol-slider"
                     min="0" max="100" step="1"
                     value={Math.round(volume * 100)}
                     onChange={onVolumeInput}
                     aria-label="SolRadio volume"
                     aria-valuemin="0"
                     aria-valuemax="100"
                     aria-valuenow={Math.round(volume * 100)}
                     data-testid="hub-sidebar-radio-volume-slider"
                     style={{ "--psr-vol-fill":
                              `${Math.round(volume * 100)}%` }} />
            </div>
          </div>
          <div className="psr__controls">
            <button type="button"
                    className="psr__play-btn"
                    onClick={togglePlay}
                    title={playing ? "Pause" : "Play"}
                    aria-label={playing ? "Pause SolRadio" : "Play SolRadio"}
                    data-testid="hub-sidebar-radio-play-btn">
              {playing ? <PauseIcon /> : <PlayIcon />}
            </button>
          </div>
        </div>
      </div>

      {/* ─── Mobile mini-player (portal) ─────────────────────────────
          Renders into `#hub-mobile-radio-slot` inside the mobile Hub
          header when it exists. Shows only cover art + play/pause and
          reuses the SAME `togglePlay` handler and audio element, so
          playback is uninterrupted across route changes and menu
          open/close. */}
      {mobileSlot && createPortal(
        <div className="psr-mini"
             data-playing={playing ? "1" : "0"}
             data-testid="hub-mobile-solradio">
          <div className="psr-mini__art" aria-hidden="true">
            {showArt
              ? <img src={meta.art} alt=""
                     onError={() => setArtFail(true)} />
              : <div className="psr-mini__art-fallback"><BarsIdle /></div>}
          </div>
          <button type="button"
                  className="psr-mini__btn"
                  onClick={togglePlay}
                  title={playing ? "Pause" : "Play"}
                  aria-label={playing ? "Pause SolRadio" : "Play SolRadio"}
                  data-testid="hub-mobile-solradio-btn">
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>
        </div>,
        mobileSlot,
      )}
    </div>
  );
}
